/**
 * Filters of the employees table (Чат 4, правка №15, MR 4). Pure, no React. [Claude] 2026-11-02
 * Any combination works together (AND); inside one list — any of the chosen values (OR).
 * The user's last filters are kept in the table layout on the server (ui-preferences "employees_table").
 *
 * filters = {
 *   departments, positions, legal_entities, schedules, genders: [values] ("" = «не заполнено»),
 *   hired_from, hired_to: "YYYY-MM-DD",
 *   tenure_min, tenure_max: months,
 *   age_min, age_max: years,
 *   birth_months: [1..12],
 *   adaptation: ["active" | "overdue" | "finished" | "none"],
 *   erp: "yes" | "no" | "",
 * }
 */

export const EMPTY_VALUE = "";
export const LIST_FILTERS = ["departments", "positions", "legal_entities", "schedules", "genders"];
export const FIELD_OF = {
    departments: "department",
    positions: "position",
    legal_entities: "legal_entity",
    schedules: "work_schedule",
    genders: "gender",
};
export const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
export const ADAPTATION_OPTIONS = [
    ["active", "Идёт"],
    ["overdue", "Просрочен срез"],
    ["finished", "Завершена"],
    ["none", "Нет адаптации"],
];
export const GENDER_LABELS = { male: "Мужской", female: "Женский" };

const DAY = /^(\d{4})-(\d{2})-(\d{2})/;

function toDate(value) {
    if (!value) return null;
    const m = String(value).match(DAY);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function months(from, to) {
    if (!from || !to || to < from) return null;
    let n = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) n -= 1;
    return Math.max(0, n);
}

const num = (value) => (value === "" || value === null || value === undefined || Number.isNaN(Number(value)) ? null : Number(value));
const list = (value) => (Array.isArray(value) ? value : []);
const norm = (value) => String(value ?? "").trim();

/** How many filters are set (for the «Фильтры (3)» button). */
export function activeFilterCount(filters = {}) {
    let count = 0;
    for (const key of [...LIST_FILTERS, "birth_months", "adaptation"]) if (list(filters[key]).length) count += 1;
    if (filters.hired_from || filters.hired_to) count += 1;
    if (num(filters.tenure_min) !== null || num(filters.tenure_max) !== null) count += 1;
    if (num(filters.age_min) !== null || num(filters.age_max) !== null) count += 1;
    if (filters.erp === "yes" || filters.erp === "no") count += 1;
    return count;
}

/** Distinct values of a field for a list filter; "" stands for «не заполнено». */
export function filterOptions(rows, field) {
    const seen = new Map();
    for (const row of rows || []) {
        const value = norm(row?.[field]);
        seen.set(value, (seen.get(value) || 0) + 1);
    }
    return [...seen.entries()]
        .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b, "ru")))
        .map(([value, count]) => ({ value, count }));
}

function adaptationState(summary) {
    if (!summary) return "none";
    if (summary.state === "finished") return "finished";
    return summary.overdue ? "overdue" : "active";
}

/** Does one employee pass the filters? ctx: { adaptationByEmployee: Map, today: Date }. */
export function matchesFilters(row, filters = {}, ctx = {}) {
    const today = ctx.today || new Date();
    for (const key of LIST_FILTERS) {
        const chosen = list(filters[key]).map(norm);
        if (chosen.length && !chosen.includes(norm(row?.[FIELD_OF[key]]))) return false;
    }
    if (filters.hired_from || filters.hired_to) {
        const hired = toDate(row?.date_hired);
        if (!hired) return false;
        const from = toDate(filters.hired_from);
        const to = toDate(filters.hired_to);
        if (from && hired < from) return false;
        if (to && hired > to) return false;
    }
    const tMin = num(filters.tenure_min);
    const tMax = num(filters.tenure_max);
    if (tMin !== null || tMax !== null) {
        const tenure = months(toDate(row?.date_hired), toDate(row?.date_fired) || today);
        if (tenure === null) return false;
        if (tMin !== null && tenure < tMin) return false;
        if (tMax !== null && tenure > tMax) return false;
    }
    const aMin = num(filters.age_min);
    const aMax = num(filters.age_max);
    if (aMin !== null || aMax !== null) {
        const birth = toDate(row?.birth_date);
        const age = birth ? Math.floor(months(birth, today) / 12) : num(row?.age);
        if (age === null || Number.isNaN(age)) return false;
        if (aMin !== null && age < aMin) return false;
        if (aMax !== null && age > aMax) return false;
    }
    const birthMonths = list(filters.birth_months).map(Number);
    if (birthMonths.length) {
        const birth = toDate(row?.birth_date);
        if (!birth || !birthMonths.includes(birth.getMonth() + 1)) return false;
    }
    const adaptation = list(filters.adaptation);
    if (adaptation.length) {
        const state = adaptationState(ctx.adaptationByEmployee?.get(row?.id));
        if (!adaptation.includes(state)) return false;
    }
    if (filters.erp === "yes" && !row?.erp_user_id) return false;
    if (filters.erp === "no" && row?.erp_user_id) return false;
    return true;
}

export function applyEmployeeFilters(rows, filters = {}, ctx = {}) {
    if (!activeFilterCount(filters)) return rows;
    return (rows || []).filter((row) => matchesFilters(row, filters, ctx));
}

/** Short chips of what is filtered: «Отдел: Логистика, IT», «Выход: с 01.01.2026». */
export function filterSummary(filters = {}) {
    const ru = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join(".") : "");
    const label = (key, value) => (value === "" ? "не заполнено" : key === "genders" ? (GENDER_LABELS[value] || value) : value);
    const names = { departments: "Отдел", positions: "Должность", legal_entities: "Юрлицо", schedules: "График", genders: "Пол" };
    const out = [];
    for (const key of LIST_FILTERS) {
        const values = list(filters[key]);
        if (values.length) out.push({ key, text: `${names[key]}: ${values.map((v) => label(key, v)).join(", ")}` });
    }
    if (filters.hired_from || filters.hired_to) {
        out.push({ key: "hired", text: `Выход: ${[filters.hired_from ? `с ${ru(filters.hired_from)}` : "", filters.hired_to ? `по ${ru(filters.hired_to)}` : ""].filter(Boolean).join(" ")}` });
    }
    const range = (min, max, unit) => [num(min) !== null ? `от ${num(min)}` : "", num(max) !== null ? `до ${num(max)}` : ""].filter(Boolean).join(" ") + ` ${unit}`;
    if (num(filters.tenure_min) !== null || num(filters.tenure_max) !== null) out.push({ key: "tenure", text: `Стаж: ${range(filters.tenure_min, filters.tenure_max, "мес.")}` });
    if (num(filters.age_min) !== null || num(filters.age_max) !== null) out.push({ key: "age", text: `Возраст: ${range(filters.age_min, filters.age_max, "лет")}` });
    if (list(filters.birth_months).length) out.push({ key: "birth_months", text: `ДР: ${list(filters.birth_months).map((m) => MONTHS[Number(m) - 1]).filter(Boolean).join(", ")}` });
    if (list(filters.adaptation).length) {
        const labels = Object.fromEntries(ADAPTATION_OPTIONS);
        out.push({ key: "adaptation", text: `Адаптация: ${list(filters.adaptation).map((v) => labels[v] || v).join(", ")}` });
    }
    if (filters.erp === "yes" || filters.erp === "no") out.push({ key: "erp", text: `Учётка ERP: ${filters.erp === "yes" ? "есть" : "нет"}` });
    return out;
}

/** Filters without one group (clicking × on a chip). */
export function withoutFilter(filters = {}, key) {
    const next = { ...filters };
    const groups = { hired: ["hired_from", "hired_to"], tenure: ["tenure_min", "tenure_max"], age: ["age_min", "age_max"] };
    for (const k of groups[key] || [key]) delete next[k];
    return next;
}
